/**
 * Online Status Detection Hook
 *
 * Provides reactive online/offline status tracking using browser APIs.
 * Uses navigator.onLine and online/offline events for detection.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

/**
 * Get the current online status from the browser
 */
function getOnlineStatus(): boolean {
  if (typeof navigator === 'undefined') {
    return true; // SSR fallback
  }
  return navigator.onLine;
}

/**
 * Subscribe to online/offline events
 */
function subscribeToOnlineStatus(callback: () => void): () => void {
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);

  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  };
}

/**
 * Hook to track online/offline status
 *
 * @returns Object with isOnline status and related utilities
 *
 * @example
 * ```tsx
 * const { isOnline, wasOffline } = useOnlineStatus();
 *
 * if (!isOnline) {
 *   return <OfflineBanner />;
 * }
 * ```
 */
export function useOnlineStatus() {
  // Use useSyncExternalStore for proper React 18 concurrent mode support
  const isOnline = useSyncExternalStore(subscribeToOnlineStatus, getOnlineStatus, () => true);

  // Track if the app was offline (for showing "back online" notifications)
  const [wasOffline, setWasOffline] = useState(false);

  // Track previous online state to detect transitions
  const prevIsOnlineRef = useRef(isOnline);

  useEffect(() => {
    const wasOnline = prevIsOnlineRef.current;
    prevIsOnlineRef.current = isOnline;

    // If we transitioned from offline to online, set wasOffline flag
    if (isOnline && !wasOnline) {
      setWasOffline(true);
    }
  }, [isOnline]);

  // Clear wasOffline flag after a delay (useful for showing "back online" toast)
  const clearWasOffline = useCallback(() => {
    setWasOffline(false);
  }, []);

  return {
    /** Whether the browser is currently online */
    isOnline,
    /** Whether the app was recently offline (for showing reconnection messages) */
    wasOffline,
    /** Clear the wasOffline flag (call after showing reconnection message) */
    clearWasOffline,
  };
}

/**
 * Non-hook utility to check online status synchronously
 * Useful for non-component code
 */
export function isCurrentlyOnline(): boolean {
  return getOnlineStatus();
}

/**
 * Subscribe to online status changes (non-hook version)
 * Returns an unsubscribe function
 *
 * @example
 * ```ts
 * const unsubscribe = subscribeOnlineStatus((isOnline) => {
 *   if (isOnline) {
 *     syncManager.startSync();
 *   }
 * });
 *
 * // Later: unsubscribe();
 * ```
 */
export function subscribeOnlineStatus(callback: (isOnline: boolean) => void): () => void {
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}

/**
 * Wait for online status (useful for sync operations)
 * Returns a promise that resolves when online
 *
 * @param timeout - Optional timeout in milliseconds (rejects if exceeded)
 *
 * @example
 * ```ts
 * await waitForOnline(30000); // Wait up to 30 seconds
 * await syncMutations();
 * ```
 */
export function waitForOnline(timeout?: number): Promise<void> {
  return new Promise((resolve, reject) => {
    if (getOnlineStatus()) {
      resolve();
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const handleOnline = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('online', handleOnline);
      resolve();
    };

    window.addEventListener('online', handleOnline);

    if (timeout) {
      timeoutId = setTimeout(() => {
        window.removeEventListener('online', handleOnline);
        reject(new Error('Timeout waiting for online status'));
      }, timeout);
    }
  });
}
